import { useGameStore } from "../../app/gameStore";
import { TILE_SIZE } from "./gridToWorld";
import { useInteractionStore } from "./interactionStore";

interface GridPickerProps {
  cols: number;
  rows: number;
}

/**
 * Invisible plane covering the board's tile area. Converts pointer
 * intersections into grid coordinates and writes them to the interaction
 * store, so hover highlight / placement-ghost components (and later, card
 * drop targets) don't each need their own raycasting.
 */
export function GridPicker({ cols, rows }: GridPickerProps) {
  const setHoveredTile = useInteractionStore((s) => s.setHoveredTile);
  const clickTile = useGameStore((s) => s.clickTile);

  // Takes just the intersection point, so both pointer and click events feed
  // it without either being cast to the other's type.
  const toTile = (e: { point: { x: number; z: number } }) => {
    const gx = Math.round(e.point.x / TILE_SIZE);
    const gy = Math.round(e.point.z / TILE_SIZE);
    if (gx < 0 || gx >= cols || gy < 0 || gy >= rows) return null;
    return { x: gx, y: gy };
  };

  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[(cols - 1) * TILE_SIZE * 0.5, 0.005, (rows - 1) * TILE_SIZE * 0.5]}
      onPointerMove={(e) => {
        e.stopPropagation();
        setHoveredTile(toTile(e));
      }}
      onPointerLeave={() => setHoveredTile(null)}
      onClick={(e) => {
        e.stopPropagation();
        const tile = toTile(e);
        if (tile) clickTile(tile);
      }}
    >
      <planeGeometry args={[cols * TILE_SIZE, rows * TILE_SIZE]} />
      <meshBasicMaterial visible={false} />
    </mesh>
  );
}
